import {
  createContext,
  PropsWithChildren,
  useContext,
  useEffect,
  useMemo,
  useReducer,
} from "react"
import { debug } from "@hornet-web-react/core/utils"
import {
  useSessionEnvironment,
  useSessionUser,
} from "@hornet-web-react/core/contexts/session"
import { useHornetSession } from "../hooks/use-hornet-session"
import { useRealtimeSession } from "../hooks/use-realtime-session"
import NiceModal from "@ebay/nice-modal-react"
import { useCookieConsent } from "@hornet-web-react/core/hooks/use-cookie-consent"
import { useLazyLoaded } from "@hornet-web-react/core/hooks/use-lazy-loaded"

type AppContainerContextState = {
  isSafeBottomPaddingDisabled: boolean
}

type AppContainerContextApi = {
  disableSafeBottomPadding: () => void
  enableSafeBottomPadding: () => void
}

const AppContainerContextStateContext = createContext<AppContainerContextState>(
  {
    isSafeBottomPaddingDisabled: false,
  }
)
const AppContainerContextApiContext = createContext<AppContainerContextApi>(
  {} as AppContainerContextApi
)

type Actions =
  | { type: "disableSafeBottomPadding" }
  | { type: "enableSafeBottomPadding" }

const reducer = (
  state: AppContainerContextState,
  action: Actions
): AppContainerContextState => {
  switch (action.type) {
    case "disableSafeBottomPadding":
      return {
        ...state,
        isSafeBottomPaddingDisabled: true,
      }

    case "enableSafeBottomPadding":
      return {
        ...state,
        isSafeBottomPaddingDisabled: false,
      }
  }
}

export const AppContainerContextProvider = ({
  children,
}: PropsWithChildren) => {
  const { isAuthenticated } = useSessionUser()
  const { markAccountAsVerified } = useHornetSession()
  const { subscribe: subscribeToSession, unsubscribe: unsubscribeFromSession } =
    useRealtimeSession({ markAccountAsVerified })
  const { isInApp } = useSessionEnvironment()
  const { isSet, isLoading } = useCookieConsent()
  const { get: getPrivacyManagerModal } = useLazyLoaded(
    () => import("../components/Modals/PrivacyManagerModal")
  )

  const [state, dispatch] = useReducer(reducer, {} as AppContainerContextState)

  const api = useMemo(() => {
    const disableSafeBottomPadding = () => {
      dispatch({ type: "disableSafeBottomPadding" })
    }

    const enableSafeBottomPadding = () => {
      dispatch({ type: "enableSafeBottomPadding" })
    }

    return {
      disableSafeBottomPadding,
      enableSafeBottomPadding,
    }
  }, [])

  // websocket subscriptions to session
  // mark as online/offline
  useEffect(() => {
    debug(
      "AppContainer: subscribeToSession, isAuthenticated: " +
        (isAuthenticated ? "yes" : "no")
    )

    if (!isAuthenticated) {
      return
    }

    subscribeToSession()

    return () => {
      unsubscribeFromSession()
    }
  }, [isAuthenticated, subscribeToSession, unsubscribeFromSession])

  useEffect(() => {
    if (!isSet && !isLoading && !isInApp) {
      void getPrivacyManagerModal((modal) => NiceModal.show(modal))
    }
  }, [isSet, isLoading, isInApp, getPrivacyManagerModal])

  return (
    <AppContainerContextApiContext.Provider value={api}>
      <AppContainerContextStateContext.Provider value={state}>
        {children}
      </AppContainerContextStateContext.Provider>
    </AppContainerContextApiContext.Provider>
  )
}

export const useAppContainerContextApi = () =>
  useContext(AppContainerContextApiContext)
export const useAppContainerContextState = () =>
  useContext(AppContainerContextStateContext)
